import { customInteraction, noticeError } from './newrelic';

interface DeviceAuthResponse {
  device_code: string;
  user_code: string;
  interval: number;
  expires_in: number;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  expires?: number;
  error?: string;
}

interface DeviceAuthState {
  deviceCode: string;
  accessToken: string | null;
  refreshToken: string | null;
  pollingInterval: number;
  pollingTimer: NodeJS.Timeout | null;
  isPolling: boolean;
  isPollingRequest: boolean;
  tokenExpiresAt: number | null;
  refreshTimer: NodeJS.Timeout | null;
  refreshRetryCount: number;
}

export class DeviceAuthManager {
  private state: DeviceAuthState = {
    deviceCode: '',
    accessToken: null,
    refreshToken: null,
    pollingInterval: 5,
    pollingTimer: null,
    isPolling: false,
    isPollingRequest: false,
    tokenExpiresAt: null,
    refreshTimer: null,
    refreshRetryCount: 0,
  };
  clientId: string;
  authEndpoint: string;

  constructor() {
    this.clientId = process.env.DEVICE_AUTH_CLIENT_ID ?? '';
    this.authEndpoint = process.env.DEVICE_AUTH_ENDPOINT ?? '';
  }

  private onUserCodeCallback: ((userCode: string, deviceEndpoint: string) => void) | null = null;
  private onAuthSuccessCallback: (() => void) | null = null;
  private onAuthErrorCallback: ((error: string) => void) | null = null;

  setCallbacks(
    onUserCode: (userCode: string, deviceEndpoint: string) => void,
    onAuthSuccess: () => void,
    onAuthError: (error: string) => void,
  ) {
    this.onUserCodeCallback = onUserCode;
    this.onAuthSuccessCallback = onAuthSuccess;
    this.onAuthErrorCallback = onAuthError;
  }

  async startAuthFlow(): Promise<void> {
    try {
      const response = await fetch(`${this.authEndpoint}/device_authorization`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: this.clientId }),
      });

      if (!response.ok) throw new Error('Failed to start device authorization');

      const data: DeviceAuthResponse = await response.json();
      this.state.deviceCode = data.device_code;
      this.state.pollingInterval = data.interval;

      const deviceEndpoint = `${this.authEndpoint}/device`;

      this.onUserCodeCallback?.(data.user_code, deviceEndpoint);
      this.startPolling();
    } catch (error) {
      this.onAuthErrorCallback?.('Failed to initiate auth flow');
    }
  }

  private startPolling(): void {
    if (this.state.isPolling) return;
    this.stopPolling();

    this.state.isPolling = true;
    this.state.pollingTimer = setInterval(async () => {
      await this.pollForToken();
    }, this.state.pollingInterval * 1000);
  }

  private stopPolling(): void {
    if (this.state.pollingTimer) {
      clearInterval(this.state.pollingTimer);
      this.state.pollingTimer = null;
    }
    this.state.isPolling = false;
  }

  private async pollForToken(): Promise<void> {
    if (!this.state.isPolling || this.state.isPollingRequest) return;

    this.state.isPollingRequest = true;

    try {
      const payload = {
        client_id: this.clientId,
        grant_type: 'urn:ietf:params:oauth:grant-type:device_code',
        device_code: this.state.deviceCode,
      };
      const response = await fetch(`${this.authEndpoint}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      this.state.isPollingRequest = false;

      if (!this.state.isPolling) return;

      customInteraction('Device auth token poll', {
        status: response.status,
        pollingInterval: this.state.pollingInterval,
      });

      if (response.status >= 500 && response.status <= 502) {
        return;
      }

      const data: TokenResponse = await response.json();

      if (response.ok) {
        this.state.accessToken = data.access_token;
        this.state.refreshToken = data.refresh_token;
        const expiresIn = data.expires_in ?? data.expires ?? 3600;
        this.state.tokenExpiresAt = Date.now() + expiresIn * 1000;
        this.stopPolling();
        this.scheduleTokenRefresh(expiresIn);
        this.onAuthSuccessCallback?.();
        return;
      }

      if (data.error === 'authorization_pending') {
        return;
      }

      if (data.error === 'slow_down') {
        this.stopPolling();
        this.state.pollingInterval += 5;
        this.startPolling();
        return;
      }

      if (data.error === 'expired_token' || data.error === 'invalid_grant') {
        this.stopPolling();
        this.onAuthErrorCallback?.('Auth flow expired or invalid');
        return;
      }
    } catch (error) {
      this.state.isPollingRequest = false;
      if (!this.state.isPolling) return;
    }
  }

  private scheduleTokenRefresh(expiresIn: number, retryDelay = 0): void {
    if (this.state.refreshTimer) {
      clearTimeout(this.state.refreshTimer);
    }

    const refreshTime = retryDelay > 0 ? retryDelay : (expiresIn - 300) * 1000;
    this.state.refreshTimer = setTimeout(async () => {
      await this.refreshAccessToken();
    }, refreshTime);
  }

  async refreshAccessToken(): Promise<boolean> {
    if (!this.state.refreshToken) return false;

    try {
      const response = await fetch(`${this.authEndpoint}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: this.clientId,
          grant_type: 'refresh_token',
          refresh_token: this.state.refreshToken,
        }),
      });

      if (!response.ok) {
        this.handleRefreshFailure();
        return false;
      }

      const data: TokenResponse = await response.json();
      this.state.accessToken = data.access_token;
      this.state.refreshToken = data.refresh_token;
      const expiresIn = data.expires_in ?? data.expires ?? 3600;
      this.state.tokenExpiresAt = Date.now() + expiresIn * 1000;
      this.state.refreshRetryCount = 0;
      this.scheduleTokenRefresh(expiresIn);
      return true;
    } catch {
      this.handleRefreshFailure();
      return false;
    }
  }

  private handleRefreshFailure(): void {
    this.state.refreshRetryCount++;
    if (this.state.refreshRetryCount <= 3) {
      const retryDelay = this.state.refreshRetryCount * 30000;
      this.scheduleTokenRefresh(0, retryDelay);
      return;
    }
    noticeError(new Error('Token refresh failed after 3 retries'), {
      retry_count: this.state.refreshRetryCount,
    });
  }

  getAccessToken(): string | null {
    return this.state.accessToken;
  }

  cleanup(): void {
    this.stopPolling();
    if (this.state.refreshTimer) {
      clearTimeout(this.state.refreshTimer);
    }
    this.state = {
      deviceCode: '',
      accessToken: null,
      refreshToken: null,
      pollingInterval: 5,
      pollingTimer: null,
      isPolling: false,
      isPollingRequest: false,
      tokenExpiresAt: null,
      refreshTimer: null,
      refreshRetryCount: 0,
    };
  }
}
